/**
 * The standard well symbology, so a well reads the same here as on a gun barrel or a
 * legend. Drawn rather than imported as art: each symbol is wanted in whatever colour
 * the rail's rule gives the well, and a sprite sheet would fix colours at build time.
 */

/** The symbols, named for what they are rather than for the status that picks them. */
export type WellSymbol =
  | 'filledCircle'
  | 'filledTriangle'
  | 'filledDiamond'
  | 'dottedDiamond'
  | 'slashedCircle'
  | 'hollowSquare'
  | 'hollowHexagon'
  | 'crossedSquare'
  | 'cross'
  | 'dottedCircle'
  | 'ringedCircle';

/**
 * The symbol a well status draws as.
 *
 * The statuses are the ones the cut carries, which are the map's own. `producing` is
 * the legend's PDP: the same well, named for its class in one place and its state in
 * the other. A status with no symbol of its own — or none at all, which a well without
 * a header has — falls back to the filled circle, so a well is never invisible for
 * want of a classification.
 *
 * The legend also covers a location to drill, valued and unvalued. The cut carries no
 * such status, so nothing maps to those two yet; the shapes exist so that whatever
 * does carry them draws them without inventing a second symbology.
 *
 * @param {string | null} status - The well's status, as the cut reports it.
 * @return {WellSymbol} The symbol to draw.
 */
export const symbolForStatus = (status: string | null): WellSymbol => {
  switch (status) {
    case 'duc':
      return 'filledTriangle';
    case 'permit':
      return 'filledDiamond';
    case 'expired_permit':
      return 'dottedDiamond';
    case 'shut_in':
      return 'slashedCircle';
    case 'inactive':
      return 'cross';
    case 'ltd':
    case 'location_to_drill':
      return 'hollowSquare';
    case 'ltd_unvalued':
      return 'crossedSquare';
    // The four Create New Well statuses share one shape (Jonathan, INS-13300); their
    // colours tell them apart.
    case 'proposal':
    case 'planned':
    case 'pre_permit':
    case 'apd':
      return 'hollowHexagon';
    default:
      return 'filledCircle';
  }
};

/**
 * The provenance an uncommitted map edit carries, as against the `Edited` the backend
 * stamps on the staged geometry it has already stored. Exported so the side that mints
 * the crossing and the side that picks its symbol cannot drift apart on the spelling.
 */
export const EDITING_GEOMETRY_KIND = 'Editing';

/**
 * The symbol a crossing draws as: its status, except the section's own Well Workspace's
 * unpromoted edit, a circle whatever the class (UAT 21) so it doesn't read as a second
 * well beside the filed trajectory.
 *
 * The workspace can show two such edits at once — the staged geometry it has saved, and
 * the edit being dragged on the map right now — and they are different answers to the
 * same question, so they take different rings. Dashed is the one on file; solid is the
 * one in hand.
 *
 * @param {string | null} status - The well's status, as the cut reports it.
 * @param {boolean} workspaceEdited - Whether the crossing is the workspace's own edit.
 * @param {string | null} geometrySourceKind - What positioned the crossing.
 * @return {WellSymbol} The symbol to draw.
 */
export const symbolForCrossing = (
  status: string | null,
  workspaceEdited = false,
  geometrySourceKind: string | null = null
): WellSymbol => {
  if (workspaceEdited) {
    return geometrySourceKind === EDITING_GEOMETRY_KIND
      ? 'ringedCircle'
      : 'dottedCircle';
  }
  return symbolForStatus(status);
};

/**
 * Whether the symbol's body is filled with the well's colour, or drawn in outline.
 *
 * A location to drill is an outline: the well is planned rather than drilled, and an
 * outline reads as such at a glance. A permit was drawn that way too until UAT asked
 * for it filled, to match the gun barrel — the two charts showing one well as a solid
 * diamond and an empty one was the harder thing to read past. The distinction is worth
 * naming because the plot's fallback marker has to make the same choice.
 *
 * @param {WellSymbol} symbol - The symbol.
 * @return {boolean} True where the body is filled.
 */
export const isFilledSymbol = (symbol: WellSymbol): boolean =>
  symbol === 'filledCircle' ||
  symbol === 'filledTriangle' ||
  symbol === 'filledDiamond';

/**
 * The size, in CSS pixels, a symbol is drawn at. Strokes reaching past the body are
 * drawn inside it, so every symbol occupies the same square whatever its shape. Sixteen
 * after UAT read thirteen as too small to tell the classes apart.
 */
export const WELL_SYMBOL_SIZE = 16;

/**
 * Symbols drawn larger than the rest, and by how much. The expired permit's diamond is
 * the only one made of gaps: a dotted outline carries a fraction of the ink the others
 * do, and at the common size it was hard to find on a white plot. The gun barrel gives
 * the same symbol the same bonus for the same reason.
 */
const SYMBOL_SIZE_BONUS: Partial<Record<WellSymbol, number>> = {
  dottedDiamond: 4,
};

/**
 * The size a symbol is drawn at. The bitmap and the marker drawn from it must ask this
 * same question: a marker sized differently from its bitmap rescales it, and a symbol
 * this thin does not survive being resampled.
 *
 * @param {WellSymbol} symbol - The symbol.
 * @return {number} Its size in CSS pixels.
 */
export const wellSymbolSize = (symbol: WellSymbol): number =>
  WELL_SYMBOL_SIZE + (SYMBOL_SIZE_BONUS[symbol] ?? 0);

/**
 * The symbol body's radius, as a share of the square it is drawn in. Inset so the
 * strokes reaching past it have room — but only just: the inset is dead space on a
 * symbol with no stroke, and at 0.29 the bodies read smaller than their box.
 *
 * Exported because a marker drawn without a sprite has to match: an unsprited circle at
 * the full box diameter comes out half again the size of a sprited one beside it.
 */
export const BODY_RADIUS_SHARE = 0.33;

/** How thick the outlines and the struck-through strokes are. */
const STROKE_WIDTH = 1.5;

/**
 * The edited geometry's radius, as a share of the square: larger than every class
 * body, since it is the one symbol that must read as a draft rather than a class,
 * and drawn broken so the size alone is not what says so.
 */
const EDITED_RADIUS_SHARE = 0.44;

// The edited geometry is a draft rather than a class, so it takes no class colour: a
// translucent Carbon gray-100 disc that reads on any band, ringed in solid gray-100.
const EDITED_FILL = 'rgba(22, 22, 22, 0.35)';
const EDITED_STROKE = '#161616';

/**
 * How far past the body a slash or an X reaches, as a share of the radius. Enough to
 * read as struck through and no more: the reach is dead space on symbols without one,
 * and at half a radius the plain circle looked a size smaller.
 */
const REACH = 0.4;

/**
 * How much of the stroke to keep between the furthest-reaching point of a symbol and
 * the edge of its bitmap, as a share of the stroke width. A mitred 90-degree corner
 * overhangs its vertex by about 0.71 of the stroke, which is the widest of the three
 * overhangs the symbols draw; this clears that.
 */
const CORNER_CLEARANCE = 0.75;

/** Dots drawn along each edge of the expired permit's diamond, corners included. */
const DIAMOND_DOTS_PER_EDGE = 3;

/**
 * The dot's radius, as a share of the body's radius. Taken off the body rather than off
 * the stroke so that it grows with the symbol: tied to the stroke, a larger diamond drew
 * the same small dots further apart and came out sparser than the one it replaced.
 * Bounded above by the gap between dots — at much above this they touch and the outline
 * goes solid, which reads as the live permit's plain diamond.
 */
const DOT_RADIUS_OF_BODY = 0.22;

/** How much of its own radius a corner dot keeps between itself and the bitmap's edge. */
const DOT_CLEARANCE = 1.1;

/**
 * How far the expired permit's dots are shaded toward black, as a share of each colour
 * channel. The symbol is the least ink of any — twelve dots with gaps between them —
 * and the seeded expired-permit grey is pale enough that at that weight the diamond was
 * hard to find on a white plot: shaded this far it carries about six times the contrast
 * against white rather than three. Far enough to find, and no further — the dots keep
 * the rule's hue, so a recoloured expired permit still reads as its own colour rather
 * than going black. The gun barrel shades the same symbol for the same reason, less
 * hard because there a formation band already gives it a stronger colour to start from.
 */
const EXPIRED_PERMIT_DARKEN = 0.6;

/**
 * Shades a colour toward black by scaling its channels, leaving any alpha alone. A
 * rule's swatch arrives as `#rgb`, `#rrggbb`, `#rrggbbaa` or `rgb()` / `rgba()`, so all
 * of those are read; anything else is handed back untouched rather than guessed at.
 *
 * @param {string} color - The colour to shade.
 * @param {number} factor - What to scale each channel by, 0 to 1.
 * @return {string} The shaded colour.
 */
const darken = (color: string, factor: number): string => {
  const scale = (channel: number) =>
    Math.max(0, Math.min(255, Math.round(channel * factor)));

  const fn = /^rgba?\(([^)]+)\)$/i.exec(color.trim());
  if (fn) {
    const parts = fn[1]!.split(',').map((part) => part.trim());
    const channels = parts.slice(0, 3).map(Number);
    if (channels.length < 3 || !channels.every(Number.isFinite)) {
      return color;
    }
    const shaded = channels.map(scale).join(', ');
    return parts[3] === undefined
      ? `rgb(${shaded})`
      : `rgba(${shaded}, ${parts[3]})`;
  }

  const hex = /^#([0-9a-f]{3,8})$/i.exec(color.trim());
  if (!hex) {
    return color;
  }
  const digits =
    hex[1]!.length === 3 || hex[1]!.length === 4
      ? hex[1]!
          .split('')
          .map((digit) => digit + digit)
          .join('')
      : hex[1]!;
  if (digits.length !== 6 && digits.length !== 8) {
    return color;
  }
  const shaded = [0, 2, 4]
    .map((at) => scale(parseInt(digits.slice(at, at + 2), 16)))
    .map((channel) => channel.toString(16).padStart(2, '0'))
    .join('');
  return `#${shaded}${digits.slice(6)}`;
};

/**
 * Draws one symbol, centred in a square of `size` device pixels.
 *
 * Takes a context rather than making its own, so the caller owns the canvas and its
 * pixel ratio: a marker drawn at device resolution and scaled down is what keeps the
 * edges clean on a retina display.
 *
 * @param {CanvasRenderingContext2D} context - Where to draw.
 * @param {object} options - The symbol, its colour, and the square to fill.
 */
export const drawWellSymbol = (
  context: CanvasRenderingContext2D,
  {
    symbol,
    color,
    size,
    scale = 1,
  }: { symbol: WellSymbol; color: string; size: number; scale?: number }
): void => {
  const centre = size / 2;
  const radius = size * BODY_RADIUS_SHARE;
  const strokeWidth = STROKE_WIDTH * scale;
  const dotRadius = radius * DOT_RADIUS_OF_BODY;
  // How far a slash, an X or a diamond's corner reaches from the centre, held clear of
  // the edge. The canvas is exactly the symbol's box, so anything drawn past it is lost
  // when the bitmap is made: at the full reach a diamond's mitred corner, the expired
  // permit's corner dot and the round cap on a slash all fall outside, and the symbol
  // comes back with its points sliced flat. The dotted diamond is measured off its dot,
  // which reaches further than the mitre the others have to clear.
  const overhang =
    symbol === 'dottedDiamond'
      ? dotRadius * DOT_CLEARANCE
      : strokeWidth * CORNER_CLEARANCE;
  const arm = Math.min(radius * (1 + REACH), centre - overhang);

  context.save();
  context.lineWidth = strokeWidth;
  context.strokeStyle = color;
  context.fillStyle = color;
  context.lineCap = 'round';

  const circle = () => {
    context.beginPath();
    context.arc(centre, centre, radius, 0, Math.PI * 2);
  };

  const square = () => {
    context.beginPath();
    context.rect(centre - radius, centre - radius, radius * 2, radius * 2);
  };

  // The diamond's corners, clockwise from the top. Set at `arm` rather than the body
  // radius: a diamond covers half the box it sits in, so at the body radius it reads a
  // size smaller than the circle beside it.
  const diamondCorners = (): [number, number][] => [
    [centre, centre - arm],
    [centre + arm, centre],
    [centre, centre + arm],
    [centre - arm, centre],
  ];

  const diamond = () => {
    const [first, ...rest] = diamondCorners();
    context.beginPath();
    context.moveTo(first![0], first![1]);
    rest.forEach(([x, y]) => context.lineTo(x, y));
    context.closePath();
  };

  const line = (fromX: number, fromY: number, toX: number, toY: number) => {
    context.beginPath();
    context.moveTo(fromX, fromY);
    context.lineTo(toX, toY);
    context.stroke();
  };

  switch (symbol) {
    case 'filledTriangle': {
      // Sat on its base, like the legend's: an equilateral triangle in the same square
      // the circles fill, so a DUC and a producing well read as the same size.
      const half = radius * 1.25;
      const height = half * 1.732;
      context.beginPath();
      context.moveTo(centre, centre - height / 2);
      context.lineTo(centre + half, centre + height / 2);
      context.lineTo(centre - half, centre + height / 2);
      context.closePath();
      context.fill();
      break;
    }
    case 'filledDiamond':
      diamond();
      context.fill();
      context.stroke();
      break;
    case 'dottedDiamond': {
      // Dotted edge by edge rather than with a dash pattern run round the path: a
      // pattern that does not divide the perimeter exactly closes short, leaving one
      // edge barer than the other three, which reads as the diamond sitting off-centre.
      // Dots rather than dashes because at marker size dashes blur into one another and
      // the diamond stops reading as a diamond.
      const corners = diamondCorners();
      context.fillStyle = darken(color, EXPIRED_PERMIT_DARKEN);
      corners.forEach(([fromX, fromY], index) => {
        const [toX, toY] = corners[(index + 1) % corners.length]!;
        for (let step = 0; step < DIAMOND_DOTS_PER_EDGE; step++) {
          const along = step / DIAMOND_DOTS_PER_EDGE;
          context.beginPath();
          context.arc(
            fromX! + (toX! - fromX!) * along,
            fromY! + (toY! - fromY!) * along,
            dotRadius,
            0,
            Math.PI * 2
          );
          context.fill();
        }
      });
      break;
    }
    case 'slashedCircle':
      circle();
      context.stroke();
      // Bottom-left to top-right, the way the legend slashes it.
      line(centre - arm, centre + arm, centre + arm, centre - arm);
      break;
    case 'cross':
      line(centre - arm, centre - arm, centre + arm, centre + arm);
      line(centre - arm, centre + arm, centre + arm, centre - arm);
      break;
    case 'hollowSquare':
      square();
      context.stroke();
      break;
    case 'hollowHexagon':
      // Pointy-top, at the body radius like the circle beside it.
      context.beginPath();
      for (let corner = 0; corner < 6; corner++) {
        // Corner 0 straight up (-90°), then every 60°: a vertex at the top and bottom.
        const angle = (corner * Math.PI) / 3 - Math.PI / 2;
        const x = centre + radius * Math.cos(angle);
        const y = centre + radius * Math.sin(angle);
        if (corner === 0) {
          context.moveTo(x, y);
        } else {
          context.lineTo(x, y);
        }
      }
      context.closePath();
      context.stroke();
      break;
    case 'crossedSquare':
      square();
      context.stroke();
      // Corner to corner of the square itself, not past it.
      line(centre - radius, centre - radius, centre + radius, centre + radius);
      line(centre - radius, centre + radius, centre + radius, centre - radius);
      break;
    case 'dottedCircle':
    case 'ringedCircle':
      context.beginPath();
      context.arc(centre, centre, size * EDITED_RADIUS_SHARE, 0, Math.PI * 2);
      context.fillStyle = EDITED_FILL;
      context.fill();
      // One disc at one size for both: the edit on file and the edit in hand are the
      // same kind of thing, and sizing them apart would read as one mattering more.
      // The ring is the whole distinction — broken while the geometry is only staged,
      // closed on the one being dragged now.
      if (symbol === 'dottedCircle') {
        context.setLineDash([2 * scale, 2 * scale]);
      }
      context.strokeStyle = EDITED_STROKE;
      context.stroke();
      break;
    default:
      circle();
      context.fill();
      break;
  }

  context.restore();
};

/**
 * A symbol as an image, ready for a SciChart sprite marker.
 *
 * The image loads asynchronously — a data URL still has to be decoded — so the caller
 * stages what it has and re-stages when the sprite arrives. Rejects rather than
 * resolving a blank image, so a failure shows up as the fallback marker instead of as
 * an invisible well.
 *
 * @param {object} options - The symbol, its colour, and the pixel ratio to draw at.
 * @return {Promise} The loaded image.
 */
export const wellSymbolImage = ({
  symbol,
  color,
  pixelRatio = 1,
}: {
  symbol: WellSymbol;
  color: string;
  pixelRatio?: number;
}): Promise<HTMLImageElement> => {
  const size = Math.round(wellSymbolSize(symbol) * pixelRatio);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (!context) {
    return Promise.reject(new Error('no 2d context for a well symbol'));
  }
  drawWellSymbol(context, { symbol, color, size, scale: pixelRatio });

  return new Promise((resolve, reject) => {
    const image = new Image(size, size);
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`could not draw ${symbol}`));
    image.src = canvas.toDataURL();
  });
};

/**
 * The cache key for one symbol in one colour. Both matter: the same status is drawn in
 * whatever colour the rail's rule gives it.
 *
 * @param {WellSymbol} symbol - The symbol.
 * @param {string} color - The colour it is drawn in.
 * @return {string} The key.
 */
export const wellSymbolKey = (symbol: WellSymbol, color: string): string =>
  `${symbol}|${color}`;

/**
 * The name a symbol is registered under in the map's own image atlas. Both halves
 * matter: the same class is drawn in whatever colour the rule gives the well.
 *
 * @param {WellSymbol} symbol - The symbol.
 * @param {string} color - The colour it is drawn in.
 * @return {string} The image name.
 */
export const mapIconName = (symbol: WellSymbol, color: string): string =>
  `cross-section-well-${wellSymbolKey(symbol, color)}`;

/** What the map needs to draw the section's wells: the images, and which well takes which. */
export interface SectionWellIcons {
  /** Each image to register, by the name the layer refers to it as. */
  icons: Map<string, { symbol: WellSymbol; color: string }>;
  /**
   * Uwi and icon name in turn, which is the shape Mapbox's `match` expression takes:
   * `['match', ['get', 'uwi'], uwi, icon, uwi, icon, …, fallback]`.
   */
  iconByUwi: string[];
  /**
   * The same wells and the same colours, paired for a `match` on `line-color`: the
   * surface hole gets a coloured symbol, the wellbore itself gets a coloured line, and
   * both read the one colour the rule gave the well.
   */
  colorByUwi: string[];
}

/**
 * The icons the section's wells are drawn with on the map.
 *
 * One well is one shape and one colour wherever it appears, so the shape comes from
 * the well's own class and the colour from the rail's rule — the same pair the plot
 * draws. Wells the rule says nothing about keep the section's default navy rather than
 * being left off the map.
 *
 * @param {Array} wells - The section's wells, with the status each carries.
 * @param {Map} colorByUwi - The rule's colour per well.
 * @param {string} fallbackColor - The colour for a well no rule speaks for.
 * @return {SectionWellIcons | null} The icons, or null with no well to draw.
 */
export const sectionWellIcons = (
  wells: Array<{ uwi: string; status?: string | null }>,
  colorByUwi: Map<string, string>,
  fallbackColor: string
): SectionWellIcons | null => {
  const icons = new Map<string, { symbol: WellSymbol; color: string }>();
  const iconByUwi: string[] = [];
  const colorPairs: string[] = [];
  const seen = new Set<string>();

  wells.forEach((well) => {
    // A well crossing the plane twice is one well on the map, and Mapbox refuses a
    // `match` that names the same value twice.
    if (seen.has(well.uwi)) {
      return;
    }
    seen.add(well.uwi);
    const color = colorByUwi.get(well.uwi) ?? fallbackColor;
    const symbol = symbolForStatus(well.status ?? null);
    const name = mapIconName(symbol, color);
    icons.set(name, { symbol, color });
    iconByUwi.push(well.uwi, name);
    colorPairs.push(well.uwi, color);
  });

  return iconByUwi.length === 0
    ? null
    : { icons, iconByUwi, colorByUwi: colorPairs };
};
